// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs, { Dayjs } from "dayjs";
import { format, isValid } from "date-fns";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input";
import AvailabilitySettings from "../../../components/AvailabilitySettings/AvailabilitySettings";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../../components/CustomFields/CustomSelect";
import PhoneInput from "react-phone-number-input";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import AddIcon from "@mui/icons-material/Add";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DoctorDocumentSave from "../../../components/CustomDocSave/DoctorDocumentSave";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetDoctorDetail,
  CreateDoctor,
  UpdateDoctor,
} from "../../../services/DoctorService";
import HospitalSave from "../../../pages/Hospital/HospitalSave";
import { GetCountryListForSelect } from "../../../services/CountryService";
import { GetSpecialityList } from "../../../services/SpecialityService";
import { GetDepartmentList } from "../../../services/DepartmentService";
import {
  GetDoctorDocumentList,
  DeleteDoctorDocument,
} from "../../../services/DoctorDocumentService";
import { GetCountryRegionListForSelect } from "../../../services/CountryRegionService";
import { PreferencesContext } from "../../../PreferenceContext";
import { UI_APP_URL, PAGE_SIZE } from "../../../services/common/const";
import {
  GetHospitalDetail,
  GetHospitalList,
} from "../../../services/HospitalService";
import TimezoneSelect, { type ITimezone } from "react-timezone-select";
import { ClearIcon } from "@mui/x-date-pickers";
import CustomTimePicker from "../../../components/TimePicker/CustomTimePicker";
import utc from "dayjs/plugin/utc"; // Import the UTC plugin
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";
import { MultiUpload } from "../../../components/MultiUpload/MultiUpload";
import { dataURLtoFile } from "../../../shared/CommonFunction";
import OutReachOPDList from "../OutReachOPD/OutReachOPDList";
import { GetOutReachLocationList } from "../../../services/OutReachOPDService";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { GetLocationList } from "../../../services/LocationService";
import { DOCTOR_TABS } from "../../../shared/Data/Doctor";

dayjs.extend(utc);

const getNamePartsFromPopup = (fullName?: string) => {
  const normalizedName = (fullName || "").trim().replace(/\s+/g, " ");
  const parts = normalizedName ? normalizedName.split(" ") : [];

  if (parts.length === 0) {
    return {
      firstName: "",
      middleName: "",
      lastName: "",
    };
  }

  if (parts.length === 1) {
    return {
      firstName: parts[0].toUpperCase(),
      middleName: "",
      lastName: "",
    };
  }

  if (parts.length === 2) {
    return {
      firstName: parts[0].toUpperCase(),
      middleName: "",
      lastName: parts[1].toUpperCase(),
    };
  }

  return {
    firstName: parts[0].toUpperCase(),
    middleName: parts.slice(1, -1).join(" ").toUpperCase(),
    lastName: parts[parts.length - 1].toUpperCase(),
  };
};

export default function DoctorSave(Props: any) {
  // PROPS
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    code,
    departmentId,
    specialityId,
  } = Props;

  // SCHEMA FOR OPTION
  interface selectOption {
    label: string;
    value: string;
  }

  // SCHEMA FOR PARAMETER
  interface Parameter {
    specialityId: string;
    consumerId: string;
    orgId: string;
    branchId: string;
    departmentId: string;
    pageIndex: number;
    pageSize: number;
    sortColumn: string;
    sortDirection: string;
    searchValue: string;
    tabType: number;
    mode: string;
  }

  // SCHEMA FOR APPOINTMENT DATA
  interface availabilityDetailData {
    availabilityDetailId: number;
    availabilityId: number;
    displayOrder: number;
    dayName: string;
    // startTime: string;
    // endTime: string;
    rowNo: number;
    startTime: Dayjs | null;
    endTime: Dayjs | null;
    mode: string;
    isDeleted: boolean;
  }
  //DEFAULT SCHEMA VALUE FOR FORM DETAIL
  const initAvailabilityDetail: availabilityDetailData = {
    availabilityDetailId: 0,
    availabilityId: 0,
    displayOrder: 0,
    dayName: "",
    // startTime: "",
    // endTime: "",
    rowNo: 0,
    startTime: null,
    endTime: null,
    mode: "",
    isDeleted: false,
  };

  // SCHEMA FOR APPOINTMENT DATA
  interface availabilityData {
    availabilityId: number;
    doctorId: number;
    durationValue: selectOption | null;
    customType: selectOption | null;
    customValue: number;
    durationType: selectOption | null;
    timezoneValue: ITimezone | null;
    availabilityDetailColl: Array<availabilityDetailData> | null;
    mode: string;
    isDeleted: boolean;
    isSundayEnable: boolean;
    isMondayEnable: boolean;
    isTuesdayEnable: boolean;
    isWednesdayEnable: boolean;
    isThursdayEnable: boolean;
    isFridayEnable: boolean;
    isSaturdayEnable: boolean;
    locationId: selectOption | null;
    locationName: string;
    locationCode: string;
  }

  const initAvailability: availabilityData = {
    availabilityId: 0,
    doctorId: 0,
    durationValue: { value: "10_MINUTES", label: "10 Minutes" },
    customValue: 10,
    customType: { value: "MINUTES", label: "Minutes" },
    durationType: null,
    timezoneValue: {
      value: "Asia/Kolkata",
      label: "(GMT+05:30) Asia/Kolkata",
    },
    availabilityDetailColl: [],
    mode: "",
    isDeleted: false,
    isSundayEnable: false,
    isMondayEnable: false,
    isTuesdayEnable: false,
    isWednesdayEnable: false,
    isThursdayEnable: false,
    isFridayEnable: false,
    isSaturdayEnable: false,
    locationId: null,
    locationName: "",
    locationCode: "",
  };

  interface outreachHospitalLocationData {
    availabilityOutreachLocationId: number;
    availabilityId: number;
    dayName: string | null;
    dayValue: string | null;
    frequencyValue: string | null;
    locationId: selectOption | null;
    LocationName: string;
    locationCode: string | null;
    frequency: selectOption | null;
    slotDuration: number | null;
    slotDurationType: selectOption | null;
    outreachHospitalLocationDetailColl: Array<outreachHospitalLocationDetailColl> | null;
    isDeleted: boolean;
    mode: string | null;
    displayOrder: number;
  }
  const initOutreachLocation: outreachHospitalLocationData = {
    availabilityOutreachLocationId: 0,
    availabilityId: 0,
    dayName: null,
    dayValue: null,
    frequencyValue: null,

    locationCode: null,
    frequency: null,
    slotDuration: null,
    slotDurationType: null,
    // outreachHospitalLocationDetailColl: [],
    outreachHospitalLocationDetailColl: [
      {
        availabilityOutreachLocationDetailId: 0,
        availabilityOutreachLocationId: 0,
        startTime: dayjs().hour(9).minute(0).toDate(),
        endTime: dayjs().hour(12).minute(0).toDate(),
        displayOrder: 0,
        parentDisplayOrder: 0,
        rowNo: 0,
        mode: "I",
        isDeleted: false,
      },
    ],
    isDeleted: false,
    mode: null,
    displayOrder: 0,
    locationId: null,
    LocationName: "",
  };

  interface outreachHospitalLocationDetailColl {
    availabilityOutreachLocationDetailId: number;
    availabilityOutreachLocationId: number;
    startTime: any;
    endTime: any;
    displayOrder: number;
    parentDisplayOrder: number;
    rowNo: number;
    isDeleted: boolean;
    mode: string;
  }

  const initOutreachHospitalLocationDetail: outreachHospitalLocationDetailColl =
    {
      availabilityOutreachLocationDetailId: 0,
      availabilityOutreachLocationId: 0,
      startTime: null,
      endTime: null,
      displayOrder: 0,
      parentDisplayOrder: 0,
      rowNo: 0,
      mode: "",
      isDeleted: false,
    };

  // SCHEMA FOR FORM
  interface formInitialValueTypes {
    departmentId: selectOption | null;
    departmentName: string | null;
    specialityId: selectOption | null;
    specialityName: string | null;
    branchId: number;
    orgId: number;
    consumerId: number;
    doctorId: Number;
    doctorTypeCode: selectOption | null;
    prefix: selectOption | null;
    hospitalId: selectOption | null;
    firstName: string;
    middleName: string;
    lastName: string;
    qualification: string;
    designation: string;
    gender: selectOption | null;
    maritalStatus: selectOption | null;
    dateOfBirth: string;
    address1: string;
    address2: string;
    city: string;
    postalCode: string;
    countryId: selectOption | null;
    countryName: string | null;
    countryRegionId: selectOption | null;
    countryRegionName: string | null;
    email: string;
    clinicEmail: string;
    primaryMobileNumber: string;
    secondaryMobileNumber: string;
    alternativeAppointmentNumber: string;
    availabilityData: availabilityData | null;
    outreachHospitalLocationData: [outreachHospitalLocationData];
    folderPath: string;
    signatureOriginalName: string;
    signatureServerName: string;
    stampOriginalName: string;
    stampServerName: string;
    StampUploadMode: string;
    SignatureUploadMode: string;

    // Token list
    refBookToken?: string | null;
    remarks?: string | null;
    ipAddress?: string | null;
    deviceInfo?: string | null;

    location: selectOption | null;
    locationId: number;
    locationCode: string;
    isDeleted: boolean;
  }
  interface OutReachLocationData {
    hospitalColl: Array<any>;
    outReachLocationColl: Array<any>;
    totalCount: number;
  }
  const popupNameParts = getNamePartsFromPopup(name);
  const initOutReachLocationData: OutReachLocationData = {
    hospitalColl: [],
    outReachLocationColl: [],
    totalCount: 0,
  };
  // DEFAULT SCHEMA VALUE FOR FORM
  const initDoctorData: formInitialValueTypes = {
    departmentId:
      isForPopUp && code?.value === "CONSULTING_DOCTOR" ? departmentId : null,
    specialityId:
      isForPopUp && code?.value === "CONSULTING_DOCTOR" ? specialityId : null,
    branchId: 0,
    orgId: 0,
    consumerId: 0,
    doctorId: 0,
    // doctorTypeCode:
    //   isForPopUp && code === "REFERRAL_DOCTOR"
    //     ? { value: "REFERRAL_DOCTOR", label: "Referral Doctor" }
    //     : null,
    doctorTypeCode: isForPopUp
      ? code === "REFERRAL_DOCTOR"
        ? { value: "REFERRAL_DOCTOR", label: "Referral Doctor" }
        : code === "DOCTOR_TO_BE_REFERRED"
          ? { value: "DOCTOR_TO_BE_REFERRED", label: "Doctor to be Referred" }
          : null
      : null,

    prefix: { value: "Dr.", label: "Dr." },
    hospitalId: null,
    // firstName: isForPopUp ? name?.toUpperCase() : "",
    // middleName: "",
    // lastName: "",
    firstName: isForPopUp ? popupNameParts.firstName : "",
    middleName: isForPopUp ? popupNameParts.middleName : "",
    lastName: isForPopUp ? popupNameParts.lastName : "",
    qualification: "",
    designation: "",
    gender: null,
    maritalStatus: null,
    dateOfBirth: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    countryId: { label: "INDIA", value: "91" },
    countryName: null,
    countryRegionId: { label: "Gujarat (24)", value: "7" },
    countryRegionName: null,
    email: "",
    clinicEmail: "",
    primaryMobileNumber: "",
    secondaryMobileNumber: "",
    alternativeAppointmentNumber: "",
    isDeleted: false,
    availabilityData: initAvailability,
    outreachHospitalLocationData: [initOutreachLocation],
    signatureOriginalName: "",
    signatureServerName: "",
    stampOriginalName: "",
    stampServerName: "",
    StampUploadMode: "I",
    SignatureUploadMode: "I",
    departmentName: null,
    specialityName: null,
    folderPath: "",
    location: null,
    locationId: 0,
    locationCode: "",
  };

  // DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
  const parameters: Parameter = {
    specialityId: "",
    consumerId: "",
    orgId: "",
    departmentId: "",
    branchId: "",
    pageIndex: 1,
    pageSize: PAGE_SIZE,
    sortColumn: "",
    sortDirection: "",
    searchValue: "",
    tabType: 0,
    mode: "DROPDOWN",
  };
  const doctorTypeOptions: selectOption[] = [
    { value: "CONSULTING_DOCTOR", label: "Consulting  Doctor" },
    { value: "REFERRAL_DOCTOR", label: "Referral Doctor" },
    { value: "DOCTOR_TO_BE_REFERRED", label: "Doctor to be Referred" },
    // { value: 'VISITING_DOCTOR', label: 'Visiting Doctor' },
  ];
  const doctorTypeOptionsReferral: selectOption[] = [
    { value: "REFERRAL_DOCTOR", label: "Referral Doctor" },
    { value: "DOCTOR_TO_BE_REFERRED", label: "Doctor to be Referred" },
  ];
  // const doctorTypeTOBEReferral: selectOption[] = [
  //   { value: "DOCTOR_TO_BE_REFERRED", label: "Doctor to be Referred" },
  // ];
  const prefixOptions: selectOption[] = [
    { value: "Dr.", label: "Dr." },
    { value: "Mr.", label: "Mr." },
    { value: "Ms.", label: "Ms." },
    { value: "Mrs.", label: "Mrs." },
    { value: "Prof.", label: "Prof." },
  ];

  const genderOptions: selectOption[] = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "OTHER", label: "Other" },
  ];

  const maritalStatusOptions: selectOption[] = [
    { value: "SINGLE", label: "Single" },
    { value: "MARRIED", label: "Married" },
    { value: "DIVORCED", label: "Divorced" },
    { value: "WIDOWED", label: "Widowed" },
    { value: "REMARRIED", label: "Remarried" },
  ];

  const durationOptions: selectOption[] = [
    { value: "5_MINUTES", label: "5 Minutes" },
    { value: "10_MINUTES", label: "10 Minutes" },
    { value: "15_MINUTES", label: "15 Minutes" },
    { value: "30_MINUTES", label: "30 Minutes" },
    { value: "45_MINUTES", label: "45 Minutes" },
    { value: "1_HOURS", label: "1 Hours" },
    { value: "CUSTOM", label: "Custom" },
  ];

  const durationTypeOptions: selectOption[] = [
    { value: "MINUTES", label: "Minutes" },
    { value: "HOURS", label: "Hours" },
  ];

  // SCHEMA
  interface Data {
    doctorDocumentId: number;
    name: string;
    registration: string;
    readonlegistration_BirthDate: string;
    expirationDate: string;
    documentName: string;
    size: string;
    uploadedDate: string;
    none: string;
  }

  type Order = "asc" | "desc" | "";

  // SCHEMA FOR TABLE
  interface EnhancedTableProps {
    numSelected: number;
    //onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: String;
    rowCount: number;
  }
  // SCHEMA FOR PARAMETER
  interface TableParameter {
    doctorDocumentId: string;
    pageIndex: number;
    pageSize: number;
    sortColumn: string;
    sortDirection: string;
    searchValue: string;
    tabType: number;
  }
  // DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
  const tableParameter: TableParameter = {
    doctorDocumentId: "",
    pageIndex: 1,
    pageSize: PAGE_SIZE,
    sortColumn: "",
    sortDirection: "",
    searchValue: "",
    tabType: 2,
  };

  // SCHEMA FOR RESULTDATA
  interface ResultData {
    doctorDocumentColl: Array<any>;
    totalCount: number;
  }

  // DEFAULT VALUES FOR RESULT DATA
  const initResultData: ResultData = {
    doctorDocumentColl: [],
    totalCount: 0,
  };

  // TABLE ROWS FOR SKELETON
  const skeletonRowCount = 5;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  const location = useLocation();
  let curRawDoctorId = urlParams.get("q");
  let currentTab = urlParams.get("t");
  let curDoctorId = 0;
  let currTabType = 0;
  // if (
  //   curRawDoctorId !== null &&
  //   curRawDoctorId !== undefined &&
  //   currentTab !== null &&
  //   currentTab !== undefined
  // ) {
  //   let decId = edService.decrypt(curRawDoctorId);
  //   if (
  //     decId !== "" &&
  //     decId !== null &&
  //     decId !== undefined &&
  //     isForPopUp === false
  //   ) {
  //     curDoctorId = +decId;
  //   }
  //   currTabType = DOCTOR_TABS.find((x) => x.code === currentTab)?.value || 0;
  // } else {
  //   navigate("/not-found");
  // }
  if (!isForPopUp) {
    if (
      curRawDoctorId !== null &&
      curRawDoctorId !== undefined &&
      currentTab !== null &&
      currentTab !== undefined
    ) {
      let decId = edService.decrypt(curRawDoctorId);

      if (
        decId !== "" &&
        decId !== null &&
        decId !== undefined &&
        isForPopUp === false
      ) {
        curDoctorId = +decId;
      }
      currTabType = DOCTOR_TABS.find((x) => x.code === currentTab)?.value || 0;
    } else {
      // navigate("/not-found");
    }
  }

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [param, setParam] = useState({ ...parameters, tabType: currTabType });
  const [countryRegionData, setCountryRegionData] = useState<any[]>([]); // REPLACE ANY WITH COUNTRY INTERFACE
  const [countryData, setCountryData] = useState<any[]>([]); // REPLACE ANY WITH STATE INTERFACE
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  const [hospitalData, setHospitalData] = useState<any[]>([]);
  const [locationData, setLocationData] = useState<any[]>([]);

  //table
  const [tableParam, setTableParam] = useState(tableParameter);
  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const [page_Index, setPage_Index] = useState(1);
  const [pageIndex, setPageIndex] = useState(1);
  const [value, setValue] = useState("");
  const [counData, setCounData] = useState(0);
  const [Curr_page, setCurrPage] = React.useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [counDataHospital, setCounDataHospital] = useState(0);
  const [totalCountHospital, setTotalCountHospital] = useState(0);
  const [pageStartIndexHospital, setPageStartIndexHospital] = useState(1);
  const [outReachLocationData, setOutReachLocationData] =
    useState<OutReachLocationData>(initOutReachLocationData);
  const [pageEndIndexHospital, setPageEndIndexHospital] = useState(1);
  const [customDuration, setCustomDuration] = React.useState(false);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [customDocPopUp, setCustomDocPopUp] = React.useState({
    doctorDocId: 0,
    popupDoctorId: curDoctorId,
    open: false,
    name: "",
    code: "",
  });
  const [tabProps, setTabProps] = useState("");
  const [time, setTime] = React.useState<Dayjs | null>(
    dayjs("2022-04-17T15:30"),
  );
  const [documentsStampFile, setDocumentsStampFile] = useState<any>([]);
  const [documentsSignatureFile, setDocumentsSignatureFile] = useState<any>([]);

  const [customHospitalPopUp, setCustomHospitalPopUp] = React.useState({
    open: false,
    name: "",
  });

  const [asyncSelectKey, setAsyncSelectKey] = React.useState({
    hospitalSearch: 1,
  });

  const handleHospitalClickOpen = (value: boolean, name: string) => {
    setCustomHospitalPopUp({ ...customHospitalPopUp, open: value, name: name });
  };
  const handleHospitalClose = () => {
    setCustomHospitalPopUp({ ...customHospitalPopUp, open: false });
  };

  const handleHospitalManagePopup = async (code: string, option: any) => {
    setCustomHospitalPopUp({ ...customHospitalPopUp, open: false });
    if (code === "HOSPITAL_CREATE") {
      methods.setValue(
        "hospitalId",
        {
          label: option?.hospitalLable,
          value: option?.hospitalId.toString(),
        },
        {
          shouldValidate: true,
        },
      );
      setIsLoading(true);
      await fetchHospital("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        hospitalSearch: asyncSelectKey.hospitalSearch + 1,
      });
      setIsLoading(false);
    }
  };

  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomDocPopUp({
      ...customDocPopUp,
      open: value,
      doctorDocId: 0,
      name: label,
      code: code,
    });
  };

  const handleClose = () => {
    setCustomDocPopUp({ ...customDocPopUp, open: false });
  };

  const handleManagePopup = async (code: string) => {
    setCustomDocPopUp({ ...customDocPopUp, open: false });
    if (code === "CREATE_OR_UPDATE") {
      setIsLoading(true);
      await getDoctorDocumentList();
      setIsLoading(false);
    } else if (code === "REFERRAL_BOOK_LINK") {
      // await saveChiefComplaint();
    }
  };

  // UPDATE CUSTOM DOC
  const editDocument = (doctorDocumentId: number) => {
    setCustomDocPopUp({
      ...customDocPopUp,
      doctorDocId: doctorDocumentId,
      open: true,
    });
  };

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    doctorTypeCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Doctor Type is required"),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when("doctorTypeCode.value", {
        is: (doctorTypeCode: string) => doctorTypeCode === "CONSULTING_DOCTOR",
        then: (schema) => schema.required("Department is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when("doctorTypeCode.value", {
        is: (doctorTypeCode: string) => doctorTypeCode === "CONSULTING_DOCTOR",
        then: (schema) => schema.required("Speciality is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    departmentName: Yup.string().when("doctorTypeCode.value", {
      is: (doctorTypeCode: string) => doctorTypeCode === "CONSULTING_DOCTOR",
      then: (schema) => schema.required("Department is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    specialityName: Yup.string().when("doctorTypeCode.value", {
      is: (doctorTypeCode: string) => doctorTypeCode === "CONSULTING_DOCTOR",
      then: (schema) => schema.required("Speciality is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    hospitalId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .when("doctorTypeCode.value", {
        is: (doctorTypeCode: string) => doctorTypeCode === "REFERRAL_DOCTOR",
        then: (schema) => schema.nullable("hospital/Clinic is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    branchId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    doctorId: Yup.number().nullable(),
    prefix: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    firstName: Yup.string().required("First  Name is required"),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().required("Last Name is required"),
    qualification: Yup.string().when("doctorTypeCode.value", {
      is: (doctorTypeCode: string) => doctorTypeCode === "CONSULTING_DOCTOR",
      then: (schema) => schema.required("Qualification is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    designation: Yup.string().when("doctorTypeCode.value", {
      is: (doctorTypeCode: string) => doctorTypeCode === "CONSULTING_DOCTOR",
      then: (schema) => schema.required("Designation is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    gender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Gender is required"),
    maritalStatus: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    dateOfBirth: Yup.string().nullable(),
    address1: Yup.string().nullable(),
    address2: Yup.string(),
    city: Yup.string().nullable(),
    postalCode: Yup.string().nullable(),
    countryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Country is required"),
    countryName: Yup.string().nullable(),
    countryRegionId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("State is required"),
    countryRegionName: Yup.string().nullable(),
    email: Yup.string().email("Invalid email").nullable(),
    clinicEmail: Yup.string().email("Invalid email").nullable(),
    primaryMobileNumber: Yup.string().nullable(),
    secondaryMobileNumber: Yup.string().nullable(),
    alternativeAppointmentNumber: Yup.string().nullable(),
    folderPath: Yup.string(),
    availabilityData: Yup.object()
      .shape({
        availabilityId: Yup.number().nullable(),
        doctorId: Yup.number().nullable(),
        durationValue: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .nullable(),
        customType: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .nullable(),
        customValue: Yup.number().nullable(),
        durationType: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .nullable(),
        timezoneValue: Yup.mixed().nullable(),
        mode: Yup.string(),
        isDeleted: Yup.boolean(),
        isSundayEnable: Yup.boolean().required(),
        isMondayEnable: Yup.boolean().required(),
        isTuesdayEnable: Yup.boolean().required(),
        isWednesdayEnable: Yup.boolean().required(),
        isThursdayEnable: Yup.boolean().required(),
        isFridayEnable: Yup.boolean().required(),
        isSaturdayEnable: Yup.boolean().required(),
        availabilityDetailColl: Yup.array()
          .of(
            Yup.object()
              .shape({
                availabilityDetailId: Yup.number().nullable(),
                availabilityId: Yup.number().nullable(),
                displayOrder: Yup.number().nullable(),
                dayName: Yup.string().nullable(),
                startTime: Yup.mixed().nullable(),
                endTime: Yup.mixed().nullable(),
                mode: Yup.string().nullable(),
                isDeleted: Yup.boolean().nullable(),
              })
              .nullable(),
          )
          .nullable(),
      })
      .nullable(),
    refBookToken: Yup.string().nullable(),

    outreachHospitalLocationData: Yup.array()
      .of(
        Yup.object()
          .shape({
            availabilityOutreachLocationId: Yup.number().nullable(),
            availabilityId: Yup.number().nullable(),
            displayOrder: Yup.number().nullable(),
            dayValue: Yup.string().nullable(),
            frequencyValue: Yup.string().nullable(),
            locationId: Yup.object()
              .shape({
                value: Yup.string(),
                label: Yup.string(),
              })
              .required("location is required")
              .nullable(),
            locationName: Yup.string().nullable(),
            locationCode: Yup.string().nullable(),
            mode: Yup.string().nullable(),
            isDeleted: Yup.boolean().nullable(),
            slotDuration: Yup.number().nullable(),
            slotDurationType: Yup.object()
              .shape({
                value: Yup.string(),
                label: Yup.string(),
              })
              .nullable(),
            outreachHospitalLocationDetailColl: Yup.array()
              .of(
                Yup.object()
                  .shape({
                    availabilityOutreachLocationDetailId:
                      Yup.number().nullable(),
                    availabilityOutreachLocationId: Yup.number().nullable(),
                    startTime: Yup.mixed().nullable(),
                    endTime: Yup.mixed().nullable(),
                    displayOrder: Yup.number().nullable(),
                    parentDisplayOrder: Yup.number().nullable(),
                    mode: Yup.string().nullable(),
                    isDeleted: Yup.boolean().nullable(),
                  })
                  .nullable(),
              )
              .nullable(),
          })
          .nullable(),
      )
      .nullable(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initDoctorData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const watchedFields = useWatch({
    control: methods.control,
  });

  const watchedAvailabilityDetailColl = useWatch({
    name: "availabilityData.availabilityDetailColl",
    control: methods.control,
  });

  const filteredAvailabilityDetailColl =
    watchedAvailabilityDetailColl?.filter(
      (x) => x?.isDeleted === false && x.mode !== "D",
    ) || [];

  const handleCopy = () => {
    const link = `${UI_APP_URL}/refer-book-entry?t=${watchedFields.refBookToken}`;
    navigator.clipboard.writeText(link);
  };
  const watchedOutreachHospitalLocationDetailColl = useWatch({
    name: "outreachHospitalLocationData",
    control: methods.control,
  });

  // GET Doctor DETAIL
  const getDoctorDetailById = async () => {
    let Doctor: any;
    let params = new URLSearchParams();
    params.append("doctorId", curDoctorId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString()); // TEMP ONLY REMOVE AFTER PIN FUNCTIONALITY COMPLETE
    params.append("orgId", userContext?.organizationId?.toString()); // TEMP ONLY REMOVE AFTER PIN
    params.append("branchId", userContext?.branchId?.toString()); // TEMP ONLY REMOVE AFTER PIN
    try {
      const res: any = await GetDoctorDetail(params);
      if (res?.data?.data) {
        Doctor = res.data.data;
        //CONVERT VALUE into Label
        const doctorType = doctorTypeOptions.find(
          (x) => x.value === Doctor.doctorTypeCode,
        );
        const genderconvertlabel = genderOptions.find(
          (x) => x.value === Doctor.gender,
        );
        const maritalStatusconvertlabel =
          maritalStatusOptions?.find(
            (x) => x.value === Doctor?.maritalStatus,
          ) || "";
        const prefixconvertlabel =
          prefixOptions?.find((x) => x.value === Doctor?.prefix) || "";

        // let durationUnitArr =
        //   durationTypeOptions.filter((x) => x.value === Doctor?.durationUnit) ||
        //   null;

        // const outreachHospitalLocationData =
        //   Doctor?.getOutreachLocationHour?.map((item: any) => {
        //     const matchedFreq = frequencyOptions.find(
        //       (opt) => opt.value === item.frequencyValue
        //     );
        //     const matchedDura = durationTypeOptions.find(
        //       (opt) => opt.value === item.slotDurationType
        //     );
        //     return {
        //       availabilityOutreachLocationId:
        //         item.availabilityOutreachLocationId || 0,
        //       dayName: item.dayValue
        //         ? {
        //             value: item.dayValue,
        //             label:
        //               item.dayValue.charAt(0).toUpperCase() +
        //               item.dayValue.slice(1).toLowerCase(),
        //           }
        //         : null,
        //       dayValue: item.dayValue || null,
        //       frequency: item.frequencyValue
        //         ? {
        //             value: item.frequencyValue,
        //             label: matchedFreq?.label || item.frequencyValue || "",
        //           }
        //         : null,
        //       frequencyValue: item.frequencyValue || null,
        //       slotDurationType: item.slotDurationType
        //         ? {
        //             value: item.slotDurationType,
        //             label: matchedDura?.label || item.slotDurationType || "",
        //           }
        //         : null,
        //       slotDuration: item.slotDuration || null,
        //       locationId: item.locationId
        //         ? {
        //             value: item.locationId,
        //             label: item.locationName,
        //           }
        //         : null,
        //       locationName: item.locationName || null,
        //       displayOrder: item.displayOrder || 0,
        //       isDeleted: item.isDeleted || false,
        //       mode: item.mode || "U",
        //       outreachHospitalLocationDetailColl:
        //         Doctor?.getOutreachLocationHourDetail
        //           ?.filter(
        //             (detail: any) =>
        //               detail.availabilityOutreachLocationId ===
        //               item.availabilityOutreachLocationId
        //           )
        //           ?.map((detail: any) => ({
        //             availabilityOutreachLocationDetailId:
        //               detail.availabilityOutreachLocationDetailId || 0,
        //             availabilityOutreachLocationId:
        //               detail.availabilityOutreachLocationId || 0,
        //             startTime: detail.startTime ? detail.startTime : null,
        //             endTime: detail.endTime ? detail.endTime : null,
        //             displayOrder: detail.displayOrder || 0,
        //             parentDisplayOrder: item.displayOrder || 0, // ? correct parent mapping
        //             isDeleted: detail.isDeleted || false,
        //             mode: detail.mode || "U",
        //           })) || [],
        //     };
        //   }) || [];
        methods.reset({
          ...initDoctorData,
          doctorId: Doctor?.doctorId || 0,
          branchId: Doctor?.branchId || 0,
          orgId: Doctor?.orgId || 0,
          consumerId: Doctor?.consumerId || 0,
          doctorTypeCode: doctorType || null,
          departmentId: Doctor?.departmentId
            ? {
                label: Doctor?.departmentName || "",
                value: Doctor?.departmentId,
              }
            : null,
          specialityId: Doctor?.specialityId
            ? {
                label: Doctor?.specialityName || "",
                value: Doctor?.specialityId,
              }
            : null,
          departmentName: Doctor?.departmentName || "",
          specialityName: Doctor?.specialityName || "",
          prefix: prefixconvertlabel || null,
          // hospitalId: Doctor?.hospitalId
          //   ? {
          //       label: Doctor?.hospitalLabel,
          //       value: Doctor?.hospitalId,
          //     }
          //   : null,
          firstName: Doctor?.firstName || "",
          middleName: Doctor?.middleName || "",
          lastName: Doctor?.lastName || "",
          qualification: Doctor?.qualification || "",
          designation: Doctor?.designation || "",
          gender: genderconvertlabel || null,
          maritalStatus: maritalStatusconvertlabel || null,
          dateOfBirth: Doctor?.dateOfBirth || "",
          email: Doctor?.email || "",
          clinicEmail: Doctor?.clinicEmail || "",
          address1: Doctor?.address1 || "",
          address2: Doctor?.address2 || "",
          city: Doctor?.city || "",
          postalCode: Doctor?.postalCode || "",
          countryId: Doctor?.countryId
            ? {
                label: Doctor?.countryName || "",
                value: Doctor?.countryId,
              }
            : null,
          countryName: Doctor?.countryName || "",
          countryRegionId: Doctor?.countryRegionId
            ? {
                label: Doctor?.countryRegionName || "",
                value: Doctor?.countryRegionId,
              }
            : null,
          countryRegionName: Doctor?.countryRegionName || "",
          primaryMobileNumber: Doctor?.primaryMobileNumber || "",
          secondaryMobileNumber: Doctor?.secondaryMobileNumber || "",
          alternativeAppointmentNumber:
            Doctor?.alternativeAppointmentNumber || "",
          // folderPath: Doctor?.folderPath || "",
          // signatureOriginalName: Doctor?.signatureOriginalName || "",
          // signatureServerName: Doctor?.signatureServerName || "",
          // stampOriginalName: Doctor?.stampOriginalName || "",
          // stampServerName: Doctor?.stampServerName || "",
          // availabilityData: {
          //   availabilityId: Doctor?.availabilityId || 0,
          //   durationValue: Doctor?.durationValue
          //     ? {
          //         value: Doctor?.durationValue,
          //         label: Doctor?.durationLabel,
          //       }
          //     : null,
          //   customValue: Doctor?.durationUnitValue || 0,
          //   customType:
          //     durationUnitArr != null
          //       ? {
          //           value:
          //             durationUnitArr != null ? durationUnitArr[0]?.value : "",
          //           label:
          //             durationUnitArr != null ? durationUnitArr[0]?.label : "",
          //         }
          //       : null,
          //   timezoneValue: Doctor?.timezoneValue
          //     ? {
          //         value: Doctor?.timezoneValue,
          //         label: Doctor?.timezoneLabel,
          //       }
          //     : null,
          //   availabilityDetailColl: Doctor?.getAvailabilityDetail || [],
          //   isSundayEnable: Doctor?.isSundayEnable || false,
          //   isMondayEnable: Doctor?.isMondayEnable || false,
          //   isTuesdayEnable: Doctor?.isTuesdayEnable || false,
          //   isWednesdayEnable: Doctor?.isWednesdayEnable || false,
          //   isThursdayEnable: Doctor?.isThursdayEnable || false,
          //   isFridayEnable: Doctor?.isFridayEnable || false,
          //   isSaturdayEnable: Doctor?.isSaturdayEnable || false,
          //   locationId: Doctor.availabilityLocationId
          //     ? {
          //         value: Doctor.availabilityLocationId,
          //         label: Doctor.availabilityLocationName,
          //       }
          //     : null,
          //   locationName: Doctor.availabilityLocationName || null,
          // },
          // refBookToken: Doctor?.refBookToken || "",
          // outreachHospitalLocationData: outreachHospitalLocationData || [],
        });

        // if (Doctor?.stampServerName) {
        //   const fileName = Doctor.stampOriginalName;
        //   const stampfile = dataURLtoFile(Doctor.stampServerImage, fileName);
        //   setDocumentsStampFile([stampfile]);
        // }
        // if (Doctor?.signatureServerName) {
        //   const fileName = Doctor.signatureOriginalName;
        //   const signaturefile = dataURLtoFile(
        //     Doctor.signatureServerImage,
        //     fileName
        //   );
        //   setDocumentsSignatureFile([signaturefile]);
        // }
        // if (Doctor?.durationValue.toUpperCase() === "CUSTOM") {
        //   setCustomDuration(true);
        // }
      }

      return Doctor;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE DOCTOR
  const saveDoctor = async (value: formInitialValueTypes) => {
    try {
      // const token = value.token || crypto.randomUUID(); // generate unique token
      // const type = value.type || "DOCTOR";
      // const expiresOn =
      //   value.expiresOn ||
      //   new Date(Date.now() + 30 * 60 * 1000).toISOString(); // 30 min expiry
      // const usedOn = value.usedOn || "";
      // const remarks = value.remarks || "Doctor token generated on save";
      // const ipAddress =
      //   value.ipAddress ||
      //   (await fetch("https://api.ipify.org?format=json")
      //     .then((r) => r.json())
      //     .then((data) => data.ip)
      //     .catch(() => "0.0.0.0"));
      // const deviceInfo =
      //   value.deviceInfo || `${navigator.userAgent} (${navigator.platform})`;

      let result: any = "";
      let formData: any = new FormData();

      // let availabilityDetailFilteredColl =
      //   value.availabilityData?.availabilityDetailColl?.filter(
      //     (x) => x.dayName !== undefined
      //   ) || null;

      // let detailColl: Array<availabilityDetailData> =
      //   availabilityDetailFilteredColl?.map((item: availabilityDetailData) => {
      //     item.startTime = item?.startTime
      //       ? new Date(item?.startTime).toLocaleTimeString()
      //       : null;
      //     item.endTime = item?.endTime
      //       ? new Date(item?.endTime).toLocaleTimeString()
      //       : null;
      //     return item;
      //   });

      // // FOR OUTREACH
      // const outreachLocationDetailData =
      //   value.outreachHospitalLocationData?.reduce((acc, curr) => {
      //     if (curr.outreachHospitalLocationDetailColl?.length) {
      //       const details = curr.outreachHospitalLocationDetailColl.map(
      //         (slot) => ({
      //           availabilityOutreachLocationDetailId:
      //             slot.availabilityOutreachLocationDetailId ?? null,
      //           availabilityOutreachLocationId:
      //             slot.availabilityOutreachLocationId ?? 0,
      //           startTime: slot.startTime
      //             ? new Date(slot?.startTime).toLocaleTimeString()
      //             : null,
      //           endTime: slot.endTime
      //             ? new Date(slot?.endTime).toLocaleTimeString()
      //             : null,
      //           isDeleted: slot.isDeleted ?? false,
      //           displayOrder: slot.displayOrder,
      //           parentDisplayOrder: slot.parentDisplayOrder,
      //           mode: slot.mode ?? "I",
      //         })
      //       );

      //       acc = [...acc, ...details];
      //     }
      //     return acc;
      //   }, []) || [];

      // const outreachLocationData =
      //   value.outreachHospitalLocationData?.map((x) => ({
      //     availabilityOutreachLocationId: x.availabilityOutreachLocationId ?? 0,
      //     locationId: x.locationId?.value ?? null,
      //     locationCode: x.locationCode ?? null,
      //     dayValue: x.dayName?.value ?? null,
      //     frequencyValue: x.frequency?.value ?? null,
      //     displayOrder: x.displayOrder ?? null,
      //     slotDuration: x.slotDuration ?? null,
      //     slotDurationType: x.slotDurationType?.value ?? null,
      //     location: null,
      //     frequency: null,
      //     dayName: null,
      //     outreachHospitalLocationDetailColl: null,
      //     mode: x.mode ?? "I",
      //     isDeleted: x.isDeleted ?? false,
      //   })) || [];

      formData.append("doctorId", value.doctorId);
      formData.append("departmentId", value.departmentId?.value || 0);
      formData.append("specialityId", value.specialityId?.value || 0);
      formData.append("departmentName", value.departmentName || "");
      formData.append("specialityName", value.specialityName || "");
      formData.append("branchId", userContext?.branchId);
      formData.append("orgId", userContext?.organizationId);
      formData.append("consumerId", userContext?.consumerId);
      // formData.append("hospitalId", value.hospitalId?.value || 0);
      formData.append("doctorTypeCode", value.doctorTypeCode?.value);
      formData.append("prefix", value.prefix?.value);
      formData.append("firstName", value.firstName?.trim());
      formData.append("middleName", value.middleName?.trim());
      formData.append("lastName", value.lastName?.trim());
      formData.append("qualification", value.qualification?.trim() || "");
      formData.append("designation", value.designation?.trim() || "");
      formData.append("gender", value.gender?.value);
      formData.append("maritalStatus", value.maritalStatus?.value);
      formData.append("dateOfBirth", value.dateOfBirth?.trim() || "");
      formData.append("address1", value.address1?.trim() || "");
      formData.append("address2", value.address2?.trim() || "");
      formData.append("city", value.city?.trim() || "");
      formData.append("postalCode", value.postalCode?.trim() || "");
      formData.append("countryRegionId", value.countryRegionId?.value);
      formData.append("countryRegionName", value.countryRegionName || "");
      formData.append("countryId", value.countryId?.value);
      formData.append("countryName", value.countryName || "");
      formData.append("email", value.email?.trim());
      formData.append("clinicEmail", value.clinicEmail?.trim());
      formData.append(
        "primaryMobileNumber",
        value.primaryMobileNumber?.trim() || "",
      );
      formData.append(
        "secondaryMobileNumber",
        value.secondaryMobileNumber?.trim() || "",
      );
      formData.append(
        "alternativeAppointmentNumber",
        value.alternativeAppointmentNumber?.trim() || "",
      );
      // FOR AVAILABILITY
      // formData.append(
      //   "availabilityId",
      //   value.availabilityData?.availabilityId || 0
      // );
      // formData.append(
      //   "durationValue",
      //   value.availabilityData?.durationValue?.value
      // );
      // formData.append(
      //   "durationLabel",
      //   value.availabilityData?.durationValue?.label
      // );
      // formData.append("durationUnitValue", value.availabilityData?.customValue);
      // formData.append(
      //   "durationUnit",
      //   value.availabilityData?.customType?.value
      // );
      // formData.append(
      //   "timezoneValue",
      //   value.availabilityData?.timezoneValue?.value
      // );
      // formData.append(
      //   "timezoneLabel",
      //   value.availabilityData?.timezoneValue?.label
      // );
      // formData.append(
      //   "availabilityDetail",
      //   detailColl ? JSON.stringify(detailColl) : ""
      // );
      // formData.append("isSundayEnable", value.availabilityData?.isSundayEnable);
      // formData.append("isMondayEnable", value.availabilityData?.isMondayEnable);
      // formData.append(
      //   "isTuesdayEnable",
      //   value.availabilityData?.isTuesdayEnable
      // );
      // formData.append(
      //   "isWednesdayEnable",
      //   value.availabilityData?.isWednesdayEnable
      // );
      // formData.append(
      //   "isThursdayEnable",
      //   value.availabilityData?.isThursdayEnable
      // );
      // formData.append("isFridayEnable", value.availabilityData?.isFridayEnable);
      // formData.append(
      //   "isSaturdayEnable",
      //   value.availabilityData?.isSaturdayEnable
      // );
      // formData.append(
      //   "availabilityData",
      //   JSON.stringify(value.availabilityData)
      // );
      // formData.append("DataManagedBy", userContext.loginDetail.userId);
      // formData.append("folderPath", value.folderPath);
      // formData.append("StampFile", documentsStampFile[0]);
      // formData.append("StampUploadMode", "I");
      // formData.append("StampOriginalName", value.stampOriginalName);
      // formData.append("StampServerName", value.stampServerName);
      // formData.append("StampUpdateName", value.stampServerName);
      // formData.append("SignatureFile", documentsSignatureFile[0]);
      // formData.append("SignatureUploadMode", "I");
      // formData.append("SignatureOriginalName", value.signatureOriginalName);
      // formData.append("SignatureServerName", value.signatureServerName);
      // formData.append("SignatureUpdateName", value.signatureServerName);

      // formData.append("token", value.token || "");
      // formData.append("type", value.type || "");
      // formData.append("expires_on", value.expiresOn || "");
      // formData.append("used_on", value.usedOn || "");
      // formData.append("remarks", value.remarks || "");
      // formData.append("ip_address", value.ipAddress || "");
      // formData.append("device_info", value.deviceInfo || "");
      // formData.append(
      //   "availabilityLocationId",
      //   value.availabilityData?.locationId?.value || "0"
      // );
      // formData.append(
      //   "availabilitylocationCode",
      //   value.availabilityData?.locationCode || ""
      // );
      // // formData.append("availabilityOutreachLocationId", 1);
      // formData.append(
      //   "outreachLocationDetail",
      //   outreachLocationData ? JSON.stringify(outreachLocationData) : ""
      // );
      // formData.append(
      //   "outreachLocationDetailData",
      //   outreachLocationDetailData
      //     ? JSON.stringify(outreachLocationDetailData)
      //     : ""
      // );

      if (curDoctorId > 0) {
        result = await UpdateDoctor(formData);
      } else {
        result = await CreateDoctor(formData);
      }
      if (result?.data) {
        // navigate(-1);
        if (!isForPopUp) {
          navigate(-1);
          await getDoctorDetailById();
        } else {
          if (result.data) {
            handleClosePopUp("DOCTOR_CREATE", result.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET COUNTRIES
  async function fetchCountry() {
    try {
      let params = new URLSearchParams();
      const countryData: any = await GetCountryListForSelect(params);
      if (countryData?.data?.data) {
        let countyCollection = countryData.data.data;
        setCountryData(countyCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH STATE FOR COUNTRY
  async function fetchCountryRegionData(countryId: string) {
    try {
      let params = new URLSearchParams();
      params.append("countryId", countryId);
      const localCountryRegionData: any =
        await GetCountryRegionListForSelect(params);
      if (localCountryRegionData?.data?.data) {
        let stateCollection = localCountryRegionData.data.data;
        setCountryRegionData(stateCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
      }
      // setIsLoading(false);
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      // setIsLoading(false);
    }
  }

  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET COMPANY NAME
  async function fetchHospital(searchValue: string, oninit: boolean) {
    try {
      let params = new URLSearchParams();
      let hospitalCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      const medicineCompanyData: any = await GetHospitalList(params);
      if (medicineCompanyData?.data?.data?.hospitalColl) {
        hospitalCollection = medicineCompanyData.data.data.hospitalColl;
        if (oninit) setHospitalData(hospitalCollection);
        let dataCount = medicineCompanyData.data?.data?.totalCount || 0;
      }
      return hospitalCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchLocation(searchValue: string, oninit: boolean) {
    try {
      let params = new URLSearchParams();
      let locationCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("doctorId", curDoctorId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      const response: any = await GetLocationList(params);
      if (response?.data?.data?.locationColl) {
        locationCollection = response.data.data.locationColl;
        if (oninit) setLocationData(locationCollection);
      }
      return locationCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const getOutReachLocationList = async () => {
    setIsLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("doctorId", curDoctorId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetOutReachLocationList(params);
      if (result?.data?.data?.outReachLocationColl) {
        const resultData: ResultData = result?.data?.data;
        let outReachLocationColl = resultData?.outReachLocationColl || [];
        let dataCount = result?.data?.data?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounDataHospital(showDataCount);
        setOutReachLocationData(resultData);
        let startIndex = (page_Index - 1) * tableParam.pageSize + 1;
        let endIndex =
          (page_Index - 1) * tableParam.pageSize +
          (outReachLocationColl?.length || 0);
        setPageStartIndexHospital(startIndex);
        setPageEndIndexHospital(endIndex);
        setTotalCountHospital(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  // FILTER RELIEVED BY
  async function filterHospital(inputValue: string) {
    let result = await fetchHospital(inputValue, false);
    return result;
  }

  // RELIEVED BY LOAD ON SEARCH
  const loadHospitalList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterHospital(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // GET HOSPITAL DETAIL BY ID
  const getHospitalDetailById = async (hospitalId) => {
    let Patient: any;
    let params = new URLSearchParams();

    params.append("hospitalId", hospitalId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetHospitalDetail(params);
      if (res?.data?.data) {
        Patient = res.data.data;
      }
      return Patient;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const handlHospitalDataChange = (Hospital: any) => {
    methods.reset(
      {
        ...methods.getValues(),
        address1: Hospital?.address1 || "",
        address2: Hospital?.address2 || "",
        city: Hospital?.city || "",
        postalCode: Hospital?.postalCode || "",
        countryId: Hospital?.countryId
          ? {
              label: Hospital?.countryLabel,
              value: Hospital?.countryId,
            }
          : {
              label: "INDIA",
              value: "91",
            },
        countryRegionId: Hospital?.countryRegionId
          ? {
              label: Hospital?.countryRegionLabel,
              value: Hospital?.countryRegionId,
            }
          : {
              label: "Gujarat (24)",
              value: "7",
            },
        clinicEmail: Hospital?.clinicEmail || "",
        primaryMobileNumber: Hospital?.primaryMobileNumber || "",
        secondaryMobileNumber: Hospital?.secondaryMobileNumber || "",
        alternativeAppointmentNumber:
          Hospital?.alternativeAppointmentNumber || "",
      },
      {
        keepErrors: true,
      },
    );
  };

  // STATE FOR STORAGE
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [doctorDocumentId, setDoctorDocumentId] = useState(0);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null,
  );

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    doctorDocumentId: any,
  ) => {
    setAnchorEl3(event.currentTarget);
    setCustomDocPopUp({ ...customDocPopUp, doctorDocId: doctorDocumentId });
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Registration #
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Registration / Birth Date
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="left"
          >
            Expiration Date
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Document Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="right"
          >
            Size
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "20%",
            }}
            align="left"
          >
            Uploaded Date
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="left"
          >
            {""}
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // GET LIST OF STAFF DOCUMENT
  const getDoctorDocumentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("doctorId", curDoctorId?.toString());
      params.append("branchId", userContext?.branchId.toString());
      params.append("consumerId", userContext?.consumerId.toString());
      params.append("orgId", userContext?.organizationId.toString());
      params.append("pageIndex", tableParam.pageIndex?.toString());
      params.append("pageSize", tableParam.pageSize?.toString());
      params.append("sortColumn", tableParam.sortColumn);
      params.append("sortDirection", tableParam.sortDirection);
      params.append("searchValue", tableParam.searchValue);
      const result: any = await GetDoctorDocumentList(params);
      if (result?.data?.data?.doctorDocumentColl) {
        const resultData: ResultData = result?.data?.data;

        let doctorDocumentColl = resultData?.doctorDocumentColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / tableParam.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (page_Index - 1) * tableParam.pageSize + 1;
        let endIndex =
          (page_Index - 1) * tableParam.pageSize +
          (doctorDocumentColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
    }
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (Curr_page !== value) {
      setCurrPage(value);
      setPage_Index(value);
      setTableParam({ ...tableParam, pageIndex: value });
    }
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value) => {
    setOpenDeleteDilog(value);
  };

  const handleToggleDay = (dayName, isChecked, startTime, endTime) => {
    const detailColl =
      methods.getValues().availabilityData?.availabilityDetailColl || [];

    if (isChecked) {
      // Check if there are no existing rows for this day
      const isDayAlreadySet = detailColl.some((row) => row.dayName === dayName);
      if (!isDayAlreadySet) {
        handleAddRow(
          dayName,
          new Date(dayjs(new Date()).format("YYYY-MM-DD") + "T09:00"),
          new Date(dayjs(new Date()).format("YYYY-MM-DD") + "T10:00"),
        ); // Pass startTime and endTime dynamically
      }
    } else {
      removeDetailField(dayName);
    }
  };

  const getCapitalize = (dayName: string) => {
    switch (dayName.toUpperCase()) {
      case "SUNDAY":
        return "Sunday";
      case "MONDAY":
        return "Monday";
      case "TUESDAY":
        return "Tuesday";
      case "WEDNESDAY":
        return "Wednesday";
      case "THURSDAY":
        return "Thursday";
      case "FRIDAY":
        return "Friday";
      case "SATURDAY":
        return "Saturday";
      default:
        return dayName.charAt(0).toUpperCase() + dayName.slice(1).toLowerCase();
    }
  };

  // ADD ROW DAY
  const handleAddRow = (dayName: string, startTime: Date, endTime: Date) => {
    const availabilityData = methods.getValues().availabilityData;
    const detailColl = availabilityData?.availabilityDetailColl || [];

    const filterData = detailColl.filter((x) => x.dayName !== undefined) || [];
    const maxRowNumber = filterData.reduce(
      (acc, curr) => Math.max(acc, curr.displayOrder),
      0,
    );

    const newRow = {
      ...initAvailabilityDetail,
      availabilityId: availabilityData?.availabilityId,
      displayOrder: filterData?.length === 0 ? 0 : maxRowNumber + 1,
      dayName: dayName,
      startTime: startTime, // Use dynamic startTime
      endTime: endTime, // Use dynamic endTime
      mode: "I", // Optional: Set mode to "A" for added row if needed
    };

    if (availabilityData)
      availabilityData.availabilityDetailColl = [...filterData, newRow];

    if (availabilityData)
      availabilityData.availabilityDetailColl =
        availabilityData?.availabilityDetailColl?.filter(
          (x) => x.dayName !== undefined,
        ) || [];

    methods.setValue("availabilityData", availabilityData, {
      shouldValidate: true,
    });
  };

  // HANDLE ROW FIELD CHANGE
  const handleDetailFieldChange = (dayName, displayOrder, field) => {
    const data = methods.getValues();
    const detailColl = data.availabilityData?.availabilityDetailColl || [];

    const currColl = detailColl.filter((data) => {
      return data.dayName !== undefined;
    });

    for (let object of currColl) {
      if (
        object.dayName === dayName &&
        object.displayOrder === displayOrder &&
        field?.toLocaleLowerCase() === "isdeleted"
      ) {
        object.isDeleted = true;
        object.mode = "D";
      }
    }

    methods.reset({
      ...methods.getValues(),
      availabilityData: {
        ...methods.getValues().availabilityData,
        availabilityDetailColl: currColl,
      },
    });
  };
  //REMOVE ROW TIME FOR DAYS
  const removeDetailField = (dayName) => {
    const data = methods.getValues();
    const detailColl = data.availabilityData?.availabilityDetailColl || [];

    for (let object of detailColl) {
      if (object.dayName === dayName) {
        object.isDeleted = true;
        object.mode = "D";
      }
    }

    methods.reset({
      ...data,
      availabilityData: {
        ...data.availabilityData,
        [`is${dayName}Enable`]: false,
        availabilityDetailColl: detailColl,
      },
    });
  };

  // RENDER EACH DAYNAME IN A LOOP
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const frequencyOptions = [
    { value: "FIRST", label: "First" },
    { value: "SECOND", label: "Second" },
    { value: "THIRD", label: "Third" },
    { value: "FOURTH", label: "Fourth" },
    { value: "LAST", label: "Last" },
  ];
  // HANDLE DELETE
  async function deleteRow(doctorDocumenttIds: any) {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        doctorDocumentIds: doctorDocumenttIds?.toString(),
        doctorId: curDoctorId?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        orgId: userContext?.organizationId?.toString(),
        branchId: userContext?.branchId?.toString(),
        isDeleted: true,
        dataManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteDoctorDocument(DeleteData);
      if (result) {
        await getDoctorDocumentList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.doctorDocumentColl?.map(
        (n: any) => n?.doctorDocumentId || 0,
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const getFormattedFileSize = (bytes, decimals = 0) => {
    if (bytes === 0) return "0 Bytes";
    // JV
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // TAB CAHNGE -- PLEASE USE PARAMETER STATE FOR ANY SEARCH OR ANYTHING THAT AFFECT LIST RESULT
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setParam({ ...param, tabType: newValue });
    const tabCode =
      DOCTOR_TABS.find((x) => x.value === newValue)?.code || "general-info";
    navigate(
      {
        pathname: location.pathname,
        search: `?q=${encodeURIComponent(
          edService.encrypt(curDoctorId) || "",
        )}&t=${tabCode}`,
      },
      { replace: true },
    ); // replace avoids adding new history entry
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await saveDoctor(data);
    setIsLoading(false);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // Signature upload
  const saveSignatureFileSelected = (files: any) => {
    setDocumentsSignatureFile(files);
  };

  // Stamp upload
  const saveStampFileSelected = (files: any) => {
    setDocumentsStampFile(files);
  };

  const removeStampFile = (file: { name: any }) => {
    const updatedFiles = documentsStampFile.filter(
      (doc: { name: any }) => doc.name !== file?.name,
    );
    setDocumentsStampFile(updatedFiles);
  };
  const removeSignatureFile = (file: { name: any }) => {
    const updatedFiles = documentsSignatureFile.filter(
      (doc: { name: any }) => doc.name !== file?.name,
    );
    setDocumentsSignatureFile(updatedFiles);
  };

  const dowloadSignatureFile = (files: { name: any }) => {
    const selectedFile = documentsSignatureFile.find(
      (doc: { name: any }) => files?.name === doc?.name,
    );

    if (selectedFile && selectedFile.preview) {
      const downloadLink = document.createElement("a");
      downloadLink.href = selectedFile.preview;
      downloadLink.download = selectedFile.name;
      downloadLink.click();
    }
  };
  // download stemp file
  const dowloadStampFile = (files: { name: any }) => {
    const selectedFile = documentsStampFile.find(
      (doc: { name: any }) => files?.name === doc?.name,
    );

    if (selectedFile && selectedFile.preview) {
      const downloadLink = document.createElement("a");
      downloadLink.href = selectedFile.preview; // Changed from `documentFile` to `preview` based on your data structure
      downloadLink.download = selectedFile.name; // Changed from `fileOriginalName` to `name` based on your data structure
      downloadLink.click();
    }
  };

  // ON SERCH
  function searchValue(val: any) {
    if (val === "") {
      setValue("");
    }
    setPage_Index(1);
    setParam({ ...param, searchValue: val.toString(), pageIndex: 1 });
  }

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchCountry();
      await fetchHospital("", true);
      await fetchLocation("", true);
      await fetchDepartment();
      await getOutReachLocationList();
      if (curDoctorId > 0) {
        const Doctor = await getDoctorDetailById();
        if (Doctor?.departmentId) {
          await fetchSpeciality(Doctor?.departmentId);
        }
        if (Doctor?.countryId) {
          await fetchCountryRegionData(Doctor?.countryId);
        }
      } else {
        if (methods.getValues()?.countryId?.value !== null) {
          await fetchCountryRegionData(methods.getValues().countryId.value);
        }
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curDoctorId]);

  useEffect(() => {
    setIsLoading(true);
    getDoctorDocumentList();
  }, [tableParam]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container>
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12}>
                <Box>
                  <Tabs
                    value={param.tabType ?? location?.state?.tabType}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    aria-label="doctor"
                    TabIndicatorProps={{ sx: { backgroundColor: " #2e67a3" } }}
                    // sx={{
                    //   "& button:hover": {
                    //     backgroundColor: "var(--opd-hover-bg, #f1f6fe)",
                    //     borderTopLeftRadius: "10px",
                    //     borderTopRightRadius: "10px",
                    //   },
                    // }}
                    sx={{
                      minWidth: "100%",
                      "& .MuiTabs-flexContainer": {
                        flexWrap: "nowrap",
                      },
                      "& .MuiTab-root": {
                        minWidth: "auto !important",
                        paddingX: "12px",
                        fontSize: { xs: "12px", sm: "14px" },
                        flexShrink: 0,
                      },
                      "& button:hover": {
                        backgroundColor: "var(--opd-hover-bg, #f1f6fe)",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      },
                    }}
                  >
                    <Tab
                      sx={{
                        textTransform: "capitalize !important",
                      }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>General Information</span>
                        </Box>
                      }
                    />
                    {methods.getValues().doctorTypeCode?.value ===
                    "CONSULTING_DOCTOR" ? (
                      <Tab
                        sx={{ textTransform: "capitalize !important" }}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>Stamp & Signature</span>
                          </Box>
                        }
                      />
                    ) : null}
                    {methods.getValues().doctorTypeCode?.value ===
                    "CONSULTING_DOCTOR" ? (
                      <Tab
                        sx={{ textTransform: "capitalize !important" }}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>Documents</span>
                          </Box>
                        }
                      />
                    ) : null}

                    {methods.getValues().doctorTypeCode?.value ===
                    "CONSULTING_DOCTOR" ? (
                      <Tab
                        sx={{ textTransform: "capitalize !important" }}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>Availability</span>
                          </Box>
                        }
                      />
                    ) : null}
                    {methods.getValues().doctorTypeCode?.value ===
                    "CONSULTING_DOCTOR" ? (
                      <Tab
                        sx={{ textTransform: "capitalize !important" }}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>Outreach OPD / Location</span>
                          </Box>
                        }
                      />
                    ) : null}
                    {/* {methods.getValues().doctorId &&
                      methods.getValues().doctorTypeCode?.value ===
                      "CONSULTING_DOCTOR" ? (
                      <Tab
                        sx={{ textTransform: "capitalize !important" }}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>Links</span>
                          </Box>
                        }
                      />
                    ) : null} */}
                  </Tabs>
                </Box>
              </Grid>
              {param.tabType === 0 && (
                <>
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">Basic Information</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.doctorTypeCode
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Doctor Type *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="doctorTypeCode"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isDisabled={curDoctorId > 0 || code}
                                  isNotCreatable={true}
                                  id={"doctorTypeCode"}
                                  FieldName={"doctorTypeCode"}
                                  // options={
                                  //   userContext.loginDetail.userRole ===
                                  //     "superadmin" || curDoctorId > 0
                                  //     ? doctorTypeOptions
                                  //     : doctorTypeOptionsReferral
                                  // }
                                  options={doctorTypeOptionsReferral}
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.doctorTypeCode?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "doctorTypeCode",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.doctorTypeCode?.message}
                          </Typography>
                        </FormControl>
                      </Grid>

                      {/* <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${methods.formState.errors.doctorTypeCode
                              ? "validation-message"
                              : ""
                              }`}
                          >
                            Doctor Type *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="doctorTypeCode"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isMulti
                                  isDisabled={curDoctorId > 0 || code}
                                  isNotCreatable={true}
                                  id={"doctorTypeCode"}
                                  FieldName={"doctorTypeCode"}
                                  options={
                                    userContext.loginDetail.userRole === "superadmin" || curDoctorId > 0
                                      ? doctorTypeOptions
                                      : doctorTypeOptionsReferral
                                  }
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector("body")}
                                  handleChange={async (selectedOptions) => {
                                    methods.setValue("doctorTypeCode", selectedOptions || [], {
                                      shouldValidate: true,
                                    });
                                  }}
                                />
                              )}
                            />

                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.doctorTypeCode?.message}
                          </Typography>
                        </FormControl>
                      </Grid> */}

                      <Grid item xs={12} sm={6} md={9}></Grid>
                      {methods.getValues("doctorTypeCode.value") ===
                        "CONSULTING_DOCTOR" && (
                        <>
                          <Grid item xs={12} sm={6} md={6}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.departmentId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Department *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="departmentId"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      isDisabled={
                                        curDoctorId > 0 || departmentId
                                      }
                                      id={"departmentId"}
                                      FieldName={"departmentId"}
                                      options={departmentData}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={async (e) => {
                                        if (
                                          methods
                                            ?.getValues()
                                            ?.departmentId?.value?.toString() !==
                                          e?.value?.toString()
                                        ) {
                                          methods.setValue(
                                            "departmentId",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "departmentName",
                                            e?.value ? e?.label : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "specialityId",
                                            null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "specialityName",
                                            null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          if (e?.value) {
                                            setIsLoading(true);
                                            await fetchSpeciality(e.value);
                                            setIsLoading(false);
                                          } else {
                                            setspecialityData([]);
                                          }
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.departmentId?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={6}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.specialityId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Speciality *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="specialityId"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      isDisabled={
                                        curDoctorId > 0 || specialityId
                                      }
                                      id={"specialityId"}
                                      FieldName={"specialityId"}
                                      options={specialityData}
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={async (e) => {
                                        if (
                                          methods
                                            ?.getValues()
                                            ?.specialityId?.value?.toString() !==
                                          e?.value?.toString()
                                        ) {
                                          methods.setValue(
                                            "specialityId",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                          methods.setValue(
                                            "specialityName",
                                            e?.value ? e?.label : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.specialityId?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      {/* {(methods.getValues("doctorTypeCode.value") ===
                        "REFERRAL_DOCTOR" ||
                        methods.getValues("doctorTypeCode.value") ===
                          "DOCTOR_TO_BE_REFERRED") && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.hospitalId
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Hospital / Clinic Name
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="hospitalId"
                                  render={({ field }) => (
                                    <CustomAsyncSelect
                                      {...field}
                                      isNotCreatable={false}
                                      id={"hospitalId"}
                                      FieldName={"hospitalId"}
                                      defaultOptions={hospitalData}
                                      openMenuOnFocus
                                      isSearchable
                                      key={asyncSelectKey?.hospitalSearch?.toString()}
                                      isClearable
                                      menuPortalTarget={document.querySelector(
                                        "body"
                                      )}
                                      loadOptions={loadHospitalList}
                                      handleChange={async (e) => {
                                        if (e?.__isNew__ === true) {
                                          handleHospitalClickOpen(
                                            true,
                                            e.label
                                          );
                                        } else {
                                          if (
                                            methods
                                              ?.getValues()
                                              ?.hospitalId?.value?.toString() !==
                                            e?.value?.toString()
                                          ) {
                                            methods.setValue(
                                              "hospitalId",
                                              e?.value ? e : null,
                                              {
                                                shouldValidate: true,
                                              }
                                            );
                                            let Hospital: any;
                                            if (e?.value) {
                                              setIsLoading(true);
                                              Hospital =
                                                await getHospitalDetailById(
                                                  e?.value
                                                );
                                              setIsLoading(false);
                                            }
                                            handlHospitalDataChange(Hospital);
                                          }
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {methods.formState.errors.hospitalId?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid item xs={0} sm={6}></Grid>
                        </>
                      )} */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.prefix
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Prefix
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="prefix"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"prefix"}
                                  FieldName={"prefix"}
                                  options={prefixOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.prefix?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "prefix",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.prefix?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.firstName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            First Name *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("firstName", {
                                onChange: (e) => {
                                  const input = e.target;
                                  const selectionStart = input.selectionStart;
                                  const selectionEnd = input.selectionEnd;

                                  // CONVERT INPUT INTO UPPERCASE LATTERS
                                  e.target.value = input.value
                                    ? input.value.toUpperCase()
                                    : "";

                                  // Restore the cursor position
                                  setTimeout(() => {
                                    input.setSelectionRange(
                                      selectionStart,
                                      selectionEnd,
                                    );
                                  });

                                  methods.setValue(
                                    "firstName",
                                    e.target.value,
                                    {
                                      shouldValidate: true,
                                    },
                                  ); // UPDATE VALUE
                                },
                              })}
                              error={!!methods.formState.errors.firstName}
                              className={`text-input ${
                                methods.formState.errors.firstName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.firstName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.middleName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Middle Name
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("middleName", {
                                onChange: (e) => {
                                  const input = e.target;
                                  const selectionStart = input.selectionStart;
                                  const selectionEnd = input.selectionEnd;

                                  // CONVERT INPUT INTO UPPERCASE LATTERS
                                  e.target.value = input.value
                                    ? input.value.toUpperCase()
                                    : "";

                                  // Restore the cursor position
                                  setTimeout(() => {
                                    input.setSelectionRange(
                                      selectionStart,
                                      selectionEnd,
                                    );
                                  });

                                  methods.setValue(
                                    "middleName",
                                    e.target.value,
                                    {
                                      shouldValidate: true,
                                    },
                                  ); // UPDATE VALUE
                                },
                              })}
                              error={!!methods.formState.errors.middleName}
                              className={`text-input ${
                                methods.formState.errors.middleName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.middleName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.lastName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Last Name *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("lastName", {
                                onChange: (e) => {
                                  const input = e.target;
                                  const selectionStart = input.selectionStart;
                                  const selectionEnd = input.selectionEnd;

                                  // CONVERT INPUT INTO UPPERCASE LATTERS
                                  e.target.value = input.value
                                    ? input.value.toUpperCase()
                                    : "";

                                  // Restore the cursor position
                                  setTimeout(() => {
                                    input.setSelectionRange(
                                      selectionStart,
                                      selectionEnd,
                                    );
                                  });

                                  methods.setValue("lastName", e.target.value, {
                                    shouldValidate: true,
                                  }); // UPDATE VALUE
                                },
                              })}
                              error={!!methods.formState.errors.lastName}
                              className={`text-input ${
                                methods.formState.errors.lastName ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.lastName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.qualification
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Qualification{" "}
                            {methods?.getValues("doctorTypeCode.value") ===
                            "CONSULTING_DOCTOR"
                              ? "*"
                              : ""}
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("qualification")}
                              error={!!methods.formState.errors.qualification}
                              className={`text-input ${
                                methods.formState.errors.qualification
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.qualification?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.designation
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Designation{" "}
                            {methods?.getValues("doctorTypeCode.value") ===
                            "CONSULTING_DOCTOR"
                              ? "*"
                              : ""}
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("designation")}
                              error={!!methods.formState.errors.designation}
                              className={`text-input ${
                                methods.formState.errors.designation
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.designation?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.gender
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Gender *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="gender"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"gender"}
                                  FieldName={"gender"}
                                  options={genderOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.gender?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "gender",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.gender?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.maritalStatus
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Marital Status
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="maritalStatus"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"maritalStatus"}
                                  FieldName={"maritalStatus"}
                                  options={maritalStatusOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.maritalStatus?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "maritalStatus",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.maritalStatus?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.dateOfBirth
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Date of Birth
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <DatePickerComponent
                              format="DD-MM-YYYY"
                              disabled={false}
                              mode="date"
                              slotProps={{ textField: { size: "small" } }}
                              value={
                                methods?.getValues()?.dateOfBirth
                                  ? dayjs(methods?.getValues()?.dateOfBirth)
                                  : null
                              }
                              onChange={(e: any) => {
                                if (e !== null && isValid(e.$d)) {
                                  let savedDateFormate: any = format(
                                    e.$d,
                                    "yyyy-MM-dd",
                                  );
                                  methods.setValue(
                                    "dateOfBirth",
                                    savedDateFormate,
                                  );
                                } else {
                                  methods.setValue("dateOfBirth", "");
                                }
                              }}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.dateOfBirth?.message}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">
                          Contact Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.address1
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Address 1{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("address1")}
                              error={!!methods.formState.errors.address1}
                              className={`text-input ${
                                methods.formState.errors.address1 ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.address1?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.address2
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Address 2{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("address2")}
                              error={!!methods.formState.errors.address2}
                              className={`text-input ${
                                methods.formState.errors.address2 ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.address2?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.city
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            City{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("city")}
                              error={!!methods.formState.errors.city}
                              className={`text-input ${
                                methods.formState.errors.city ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.city?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.postalCode
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Postal Code
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              id="postalCode"
                              size="small"
                              {...methods.register("postalCode")}
                              error={!!methods.formState.errors.postalCode}
                              className={`text-input ${
                                methods.formState.errors.postalCode
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.postalCode?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Country *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryId"}
                                  FieldName={"countryId"}
                                  options={countryData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.countryId?.value?.toString() ==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "countryId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryName",
                                        e?.value ? e?.label : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryRegionId",
                                        null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryRegionName",
                                        "",
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      if (e?.value !== null) {
                                        setIsLoading(true);
                                        await fetchCountryRegionData(e?.value);
                                        setIsLoading(false);
                                      } else {
                                        setCountryRegionData([]);
                                      }
                                    }
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.postalCode
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryRegionId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            State *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryRegionId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryRegionId"}
                                  FieldName={"countryRegionId"}
                                  options={countryRegionData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    methods.setValue(
                                      "countryRegionId",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                    methods.setValue(
                                      "countryRegionName",
                                      e?.value ? e?.label : null,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.countryId
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryRegionId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.email
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Personal Email{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("email")}
                              error={!!methods.formState.errors.email}
                              className={`text-input ${
                                methods.formState.errors.email ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.email?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.clinicEmail
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Clinic/Hospital Email{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("clinicEmail")}
                              error={!!methods.formState.errors.clinicEmail}
                              className={`text-input ${
                                methods.formState.errors.clinicEmail
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.clinicEmail?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text ${
                              methods.formState.errors.primaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start", // Align items at the top
                              gap: "4px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <WhatsAppIcon sx={{ fontSize: 18 }} />
                              <span>Personal Mobile Number</span>
                            </Box>

                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                              sx={{ position: "relative", top: "-2px" }}
                            />
                          </FormLabel>

                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="primaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              {...methods.register("primaryMobileNumber")}
                              error={
                                methods.formState.errors.primaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.primaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              onChange={(e) => {
                                methods.setValue(
                                  "primaryMobileNumber",
                                  e || "",
                                  { shouldValidate: true },
                                );
                              }}
                              value={
                                methods.getValues()?.primaryMobileNumber || ""
                              }
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.primaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.secondaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Appointment Mobile Number
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                              sx={{ position: "relative", top: "-2px" }}
                            />
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="secondaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              {...methods.register("secondaryMobileNumber")}
                              error={
                                methods.formState.errors.secondaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.secondaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              value={
                                methods.getValues()?.secondaryMobileNumber || ""
                              }
                              onChange={(e) => {
                                methods.setValue(
                                  "secondaryMobileNumber",
                                  e || "",
                                );
                              }}
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.secondaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors
                                .alternativeAppointmentNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Alternative Appointment Number
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={isLoading}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              id="landLineNumber"
                              size="small"
                              {...methods.register(
                                "alternativeAppointmentNumber",
                                {
                                  required: false,
                                },
                              )}
                              error={
                                !!methods.formState.errors
                                  .alternativeAppointmentNumber
                              }
                              className={`text-input ${
                                methods.formState.errors
                                  .alternativeAppointmentNumber
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {
                            methods.formState.errors
                              .alternativeAppointmentNumber?.message
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              {param.tabType === 1 && (
                <Grid item xs={12} className={"group-container"}>
                  <Grid item container xs={12} spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel className="fileds-label-text">
                          Upload Stamp
                        </FormLabel>
                        <MultiUpload
                          onDropandSelect={saveStampFileSelected}
                          editFile={documentsStampFile}
                          MAX_COUNT={1}
                          onFileDownload={dowloadStampFile}
                          onFileRemove={(file: any) => {
                            removeStampFile(file);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel className="fileds-label-text">
                          Upload Signaure
                        </FormLabel>
                        <MultiUpload
                          onDropandSelect={saveSignatureFileSelected}
                          editFile={documentsSignatureFile}
                          MAX_COUNT={1}
                          onFileDownload={dowloadSignatureFile}
                          onFileRemove={(file: any) => {
                            removeSignatureFile(file);
                          }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {param.tabType === 2 && (
                <>
                  <Grid container className="list-container">
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item>
                        <Typography variant="h6">Documents</Typography>
                      </Grid>
                      <Grid item>
                        <Button
                          color="primary"
                          variant="contained"
                          // onClick={() => handleClickOpen(true)}
                          onClick={() =>
                            setCustomDocPopUp({
                              ...customDocPopUp,
                              open: true,
                              code: "CREATE_OR_UPDATE",
                              name: "",
                            })
                          }
                          size="medium"
                          startIcon={<AddIcon />}
                          disabled={curDoctorId <= 0}
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12} mt={2}>
                      <TableContainer>
                        <Table>
                          <EnhancedTableHead
                            numSelected={
                              resultData?.doctorDocumentColl?.length || 0
                            }
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleCheckSelectAll}
                            rowCount={resultData?.doctorDocumentColl?.length}
                          />
                          <TableBody>
                            {isLoading ? (
                              Array(skeletonRowCount)
                                ?.fill(0)
                                ?.map((skeletonRow, index) => {
                                  return (
                                    <TableRow
                                      key={"skeleton-row" + index?.toString()}
                                    >
                                      <TableCell colSpan={8}>
                                        <SkeletonWrapper
                                          isLoading={isLoading}
                                          className="skeleton-input"
                                        >
                                          <span></span>
                                        </SkeletonWrapper>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                            ) : (
                              <>
                                {stableSort(
                                  resultData?.doctorDocumentColl,
                                  getComparator(order, orderBy),
                                )
                                  .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage,
                                  )
                                  .map((item: any, index: any) => {
                                    return (
                                      <TableRow
                                        key={item?.doctorDocumentId?.toString()}
                                      >
                                        <TableCell
                                          onClick={() =>
                                            editDocument(item?.doctorDocumentId)
                                          }
                                        >
                                          <Typography
                                            className="text-link"
                                            sx={{ cursor: "pointer" }}
                                          >
                                            {item.documentName}
                                          </Typography>
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.registrationNumber}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {format(
                                            new Date(item.registrationDate),
                                            "dd-MM-yyyy",
                                          )}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.expirationDate
                                            ? format(
                                                new Date(item.expirationDate),
                                                "dd-MM-yyyy",
                                              )
                                            : ""}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.originalFileName}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                          align="right"
                                        >
                                          {item.fileSize === null
                                            ? "-"
                                            : getFormattedFileSize(
                                                item.fileSize,
                                              )}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.uploadedDate}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Tooltip title="More" placement="top">
                                            <IconButton
                                              onClick={(e: any) => {
                                                handleOpenMore(
                                                  e,
                                                  item?.doctorDocumentId || 0,
                                                );
                                              }}
                                            >
                                              <MoreVertRoundedIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                {resultData?.doctorDocumentColl?.length <=
                                  0 && (
                                  <TableRow>
                                    <TableCell align="center" colSpan={8}>
                                      No record(s) found
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12} paddingTop={1}>
                      {resultData?.doctorDocumentColl?.length > 0 && (
                        <Box className="pagination">
                          <Box className="pagination-label">
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-pagination-label"
                            >
                              {pageStartIndex} - {pageEndIndex} of {totalCount}
                            </SkeletonWrapper>
                          </Box>
                          <Box className="pagination-stack">
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-pagination-stack"
                            >
                              <Stack component="div">
                                <Pagination
                                  color="primary"
                                  count={counData} // NUMBER OF PAGES
                                  page={Curr_page} // CURRENT PAGE
                                  onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                                />
                              </Stack>
                            </SkeletonWrapper>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
              {param.tabType === 3 && (
                <>
                  <AvailabilitySettings
                    methods={methods}
                    param={param}
                    isLoading={isLoading}
                    durationOptions={durationOptions}
                    durationTypeOptions={durationTypeOptions}
                    locationData={locationData}
                    daysOfWeek={daysOfWeek}
                    frequencyOptions={frequencyOptions}
                    filteredAvailabilityDetailColl={
                      filteredAvailabilityDetailColl
                    }
                    outreachHospitalLocationDetailColl={
                      watchedOutreachHospitalLocationDetailColl
                    }
                    handleToggleDay={handleToggleDay}
                    handleAddRow={handleAddRow}
                    handleDetailFieldChange={handleDetailFieldChange}
                    customDuration={customDuration}
                    setCustomDuration={setCustomDuration}
                  />
                </>
              )}
              {param.tabType === 4 && (
                <>
                  <OutReachOPDList
                    hospitalData={outReachLocationData?.outReachLocationColl}
                    isLoading={isLoading}
                    value={value}
                    setValue={setValue}
                    searchValue={searchValue}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    Curr_page={Curr_page}
                    counDataHospital={counDataHospital}
                    pageStartIndexHospital={pageStartIndexHospital}
                    pageEndIndexHospital={pageEndIndexHospital}
                    totalCountHospital={totalCountHospital}
                    order={order}
                    orderBy={orderBy}
                    handleChangePageNew={handleChangePageNew}
                    curDoctorId={curDoctorId}
                    getOutReachLocationList={getOutReachLocationList}
                  />
                </>
              )}
              {param.tabType === 5 && (
                <>
                  <Grid container gap={2}>
                    <Grid item xs={12} mb={3} className={"group-container"}>
                      <Grid container item spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="h6">Referral Book</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <Button
                            variant="text"
                            disabled={isLoading}
                            startIcon={
                              <SendIcon
                                sx={{
                                  color: "gray",
                                  ml: 0.7,
                                  mb: 0.5,
                                  transform: "rotate(-40deg)",
                                }}
                              />
                            }
                            onClick={async (e) => {
                              setIsLoading(true);
                              await getDoctorDetailById();
                              setCustomDocPopUp({
                                ...customDocPopUp,
                                open: true,
                                name: "copyLink",
                                code: "REFERRAL_BOOK_LINK",
                              });
                              setIsLoading(false);
                            }}
                          >
                            <span
                              className="add-icon"
                              style={{
                                margin: "0 0 -3px -3px",
                              }}
                            >
                              Referral Book Link
                            </span>
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading}
                    onClick={(e) => {
                      console.log(methods.formState.errors);
                    }}
                  >
                    {curDoctorId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>

        <Dialog
          className="dialog1"
          open={
            customDocPopUp.open && customDocPopUp.code === "CREATE_OR_UPDATE"
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px", // Adjust the width as needed
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Document
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent
            //className="Investigation-Edit main-dialog-background-color"
            sx={{ padding: "12px 24px" }}
          >
            <DoctorDocumentSave
              // handleClosePopUp={(code) => {
              //   handleManagePopup(code);
              // }}
              handleClosePopUp={handleManagePopup}
              isForPopUp={customDocPopUp.open}
              popUpdoctorDocId={customDocPopUp?.doctorDocId || 0}
              popupDoctorId={customDocPopUp?.popupDoctorId || 0}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customHospitalPopUp.open}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleHospitalClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "720px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Hospital/Clinic
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <HospitalSave
              handleClosePopUp={(code, company) => {
                handleHospitalManagePopup(code, company);
              }}
              isForPopUp={customHospitalPopUp.open}
              name={customHospitalPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
      </Box>
      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setCustomDocPopUp({ ...customDocPopUp, open: true });
              }}
            >
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              className="delete-container"
              onClick={() => {
                handleDeleteDilog(true);
              }}
            >
              Delete
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this Doctor Document ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(customDocPopUp.doctorDocId);
            }}
            className="button-delete"
          >
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* REFERRAL BOOK LINK */}
      <Dialog
        className="dialog1"
        open={
          customDocPopUp.open && customDocPopUp.code === "REFERRAL_BOOK_LINK"
        }
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen
        PaperProps={{
          sx: {
            width: "600px",
            height: "auto",
            maxWidth: "none",
            textAlign: "center",
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              backgroundColor: "#eff3fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <QuestionMarkIcon sx={{ fontSize: 40, color: "#285cdb" }} />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "20px !important",
            }}
          >
            Share your Refer Book
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
              mt: -1,
            }}
          >
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod <br />
            tempor incididunt ut labore et dolore magna aliqua.
          </Typography>

          <TextField
            fullWidth
            value={`${UI_APP_URL}/refer-book-entry?t=${watchedFields.refBookToken}`}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip title={"Copy link"} placement="top">
                  <IconButton onClick={handleCopy}>
                    <ContentCopyIcon sx={{ color: "#1976d2" }} />
                  </IconButton>
                </Tooltip>
              ),
            }}
            sx={{
              backgroundColor: "#f8f9fb",
              borderRadius: "6px",
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            pb: 3,
          }}
        >
          <Button
            variant="contained"
            className="primary-button"
            onClick={handleClose}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
